<template>
  <div
    v-if="customIsVisible && usps.length > 0"
    :class="[backgroundColor, bottomMargin, verticalPadding, background]"
    class="usps"
  >
    <div :data-component-id="componentId" class="mx-auto w-full max-w-screen-2xl px-5 md:px-12">
      <VcTypography v-if="title" :tag="heading" :anchor-id="title" class="w-full mb-8">
        {{ title }}
      </VcTypography>
      <div class="relative">
        <VcNavButton
          v-if="hasNavigation && !useAutoplay"
          class="usp_prev absolute -left-3 lg:-left-10 top-3 z-[3]"
          :label="$t('common.buttons.previous')"
          size="xs"
          direction="left"
        />
        <Swiper
          :key="swiperKey"
          :slides-per-view="usps.length < uspsPerView ? usps.length : uspsPerView"
          :space-between="1"
          class="w-full"
          :modules="modules"
          :navigation="uspNavigationOptions"
          :autoplay="hasNavigation && useAutoplay ? { delay: 2500, disableOnInteraction: false } : false"
          :loop="hasNavigation && useAutoplay"
        >
          <SwiperSlide v-for="(usp, index) in usps" :key="index">
            <div class="flex flex-row items-center justify-start w-full" :class="{ 'justify-center': hasNavigation }">
              <!-- Fixed width container for image, no shrinking -->
              <div class="w-[60px] shrink-0 flex justify-center">
                <VcImage :src="usp.uspImage" class="w-[50px] h-[50px]" lazy />
              </div>

              <!-- Fill remaining space -->
              <div class="flex flex-col min-w-0 ml-4">
                <div class="font-semibold truncate text-lg">{{ usp.uspText }}</div>
                <div v-if="usp.uspSubtext" class="font-normal truncate text-base">{{ usp.uspSubtext }}</div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
        <VcNavButton
          v-if="hasNavigation && !useAutoplay"
          class="usp_next absolute -right-3 lg:-right-10 top-3 z-[3]"
          :label="$t('common.buttons.next')"
          size="xs"
          direction="right"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, getCurrentInstance } from "vue";
import { Navigation, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

type UspType = {
  uspText: string;
  uspSubtext?: string;
  uspImage: string;
  uspActive: boolean;
};

export interface IProps {
  id: string;
  heading?: string;
  title?: string;
  uspItems: Array<UspType>;
  useAutoplay?: boolean;

  background?: string;
  model: any;
  settings: any;

  // Innovadis custom: Use custom visibility setting
  customIsVisible: boolean;

  // Innovadis custom: Use custom layout settings
  backgroundColor: string;
  bottomMargin: string;
  verticalPadding: string;
}

const props = withDefaults(defineProps<IProps>(), {
  heading: "h2",
  useAutoplay: false,
});

const modules = [Navigation, Autoplay];
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const isTablet = breakpoints.smaller("lg");
const uspsPerView = computed(() => (isMobile.value ? 1 : isTablet.value ? 2 : 4));

// Reinitialize slider based on itemsPerView (for switch between viewports)
const swiperKey = computed(() => `usp-swiper-${componentId.value}-${uspsPerView.value}`);

const uspNavigationOptions = computed(() => {
  return {
    prevEl: `[data-component-id="${componentId.value}"] .usp_prev`,
    nextEl: `[data-component-id="${componentId.value}"] .usp_next`,
  };
});

const usps = computed<Array<UspType>>(() => {
  return props.uspItems.filter((item) => item.uspActive === true);
});

const hasNavigation = computed(() => usps.value.length > uspsPerView.value);

const componentId = computed(() => {
  return getCurrentInstance()?.uid;
});
</script>
